import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Typography,
  Box,
  Alert,
  CircularProgress,
  Button,
  Stack,
  Collapse,
  Card,
  CardContent,
  Snackbar,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import {
  Settings as SettingsIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material'
import { SidebarLayout } from '@/components/Layout'
import { 
  CalendarGrid, 
  ListingMultiSelect, 
  DaySummaryPanel, 
  NavigationDemo, 
  CalendarSyncSettings, 
  CalendarGridSkeleton,
  CalendarErrorBoundary,
  CalendarErrorDisplay,
  OfflineDetector,
  SyncProgressIndicator,
  FiltersSkeleton,
  LoadingMessage,
  EmptyCalendarState
} from '../components/Calendar'
import { CalendarBooking } from '../types'
import { useCalendarState } from '../hooks/useCalendarState'
import { useRetry, retryConditions } from '../hooks/useRetry'
import { formatMonthYear } from '../utils/calendar-utils'

export default function Calendar() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [showNavigationDemo, setShowNavigationDemo] = useState(false);
  const [showSyncSettings, setShowSyncSettings] = useState(false);
  const [syncProgress, setSyncProgress] = useState<any[]>([]);
  const [dismissedErrors, setDismissedErrors] = useState<Set<string>>(new Set());
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Use the comprehensive calendar state management hook
  const {
    state: {
      currentMonth,
      selectedDate,
      selectedListings,
      showDaySummary,
      includeExternal,
    },
    actions: {
      setIncludeExternal,
      handleDateSelect,
      handleCloseDaySummary,
      handleMonthChange,
      handleListingFilterChange,
    },
    data: {
      listings,
      allBookings,
      conflicts,
      bookingsForSelectedDate,
    },
    loading: {
      isLoadingListings,
      isLoadingBookings,
      listingsError,
      bookingsError,
      hasError,
    },
    invalidateBookings,
  } = useCalendarState({
    debounceMs: 300,
    enableUrlSync: true,
    enablePrefetching: true,
    autoSelectAllListings: true,
  });

  // Retry mechanism for failed operations
  const { execute: retryLoadBookings, state: retryState } = useRetry(
    async () => {
      await invalidateBookings();
    },
    {
      maxAttempts: 3,
      baseDelay: 2000,
      retryCondition: retryConditions.temporaryErrors,
      onRetry: (attempt, error) => {
        console.log(`Retrying calendar data load (attempt ${attempt}):`, error);
      },
    }
  );

  const handleBookingClick = (booking: CalendarBooking) => {
    // Navigate to booking details page with calendar context
    navigate(`/bookings/${booking.bookingId}`, {
      state: {
        fromCalendar: true,
        selectedDate: selectedDate?.toISOString(),
        currentMonth: currentMonth.toISOString(),
        selectedListings: selectedListings,
      },
    });
  };



  const handleSyncComplete = () => {
    // Invalidate calendar bookings when sync completes
    invalidateBookings();
    setSuccessMessage('Calendar sync completed successfully');
    setShowSuccessMessage(true);
  };

  const handleRetryBookings = async () => {
    try {
      await retryLoadBookings();
      setSuccessMessage('Calendar data refreshed successfully');
      setShowSuccessMessage(true);
    } catch (error) {
      console.error('Failed to retry loading bookings:', error);
    }
  };

  const handleDismissError = (errorType: string) => {
    setDismissedErrors(prev => new Set(prev).add(errorType));
  };

  const handleOnline = () => {
    // Refresh data when coming back online
    invalidateBookings();
  };

  const handleSyncProgress = (progress: any[]) => {
    setSyncProgress(progress);
  };

  const handleCancelSync = (syncId: string) => {
    // Implementation would cancel the sync operation
    console.log('Cancelling sync:', syncId);
  };

  const handleRetrySync = (listingId: string) => {
    // Implementation would retry sync for specific listing
    console.log('Retrying sync for listing:', listingId);
  };

  const handleDismissSync = (syncId: string) => {
    setSyncProgress(prev => prev.filter(sync => sync.syncId !== syncId));
  };

  return (
    <CalendarErrorBoundary
      resetKeys={[currentMonth.toISOString(), selectedListings.join(',')]}
      resetOnPropsChange={false}
      maxRetries={3}
    >
      <OfflineDetector onOnline={handleOnline}>
        <SidebarLayout>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4 }}>
            <Box>
              <Typography variant={isMobile ? 'h5' : 'h4'} component="h1" gutterBottom>
                Calendar
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                View and manage your bookings in a calendar format
              </Typography>
              {!isLoadingListings && listings.length > 0 && (
                <Typography variant="body2" color="text.secondary">
                  {listings.length} listing{listings.length !== 1 ? 's' : ''} available
                  {selectedListings.length > 0 && ` • ${selectedListings.length} selected`}
                </Typography>
              )}
            </Box>
            <Stack direction="row" spacing={2} sx={{ display: { xs: 'none', sm: 'flex' }, flexWrap: 'wrap', gap: 1 }}>
              {hasError && (
                <Button
                  variant="contained"
                  onClick={handleRetryBookings}
                  size="small"
                  startIcon={<RefreshIcon />}
                  disabled={retryState.isRetrying}
                >
                  {retryState.isRetrying ? 'Retrying...' : 'Retry'}
                </Button>
              )}
              {listings.length > 0 && (
                <>

                  <Button
                    variant="outlined"
                    onClick={() => setShowSyncSettings(!showSyncSettings)}
                    size="small"
                    startIcon={<SettingsIcon />}
                    color={showSyncSettings ? 'secondary' : 'primary'}
                  >
                    {showSyncSettings ? 'Hide' : 'Show'} Sync Settings
                  </Button>
                </>
              )}
              <Button
                variant="outlined"
                onClick={() => setShowNavigationDemo(!showNavigationDemo)}
                size="small"
                color={showNavigationDemo ? 'secondary' : 'primary'}
              >
                {showNavigationDemo ? 'Hide' : 'Show'} Navigation Demo
              </Button>
            </Stack>
          </Box>

          {/* Error Display */}
          {listingsError && !dismissedErrors.has('listings') && (
            <Box sx={{ mb: 2 }}>
              <CalendarErrorDisplay
                error={listingsError}
                onRetry={handleRetryBookings}
                onDismiss={() => handleDismissError('listings')}
                compact
              />
            </Box>
          )}

          {bookingsError && !dismissedErrors.has('bookings') && (
            <Box sx={{ mb: 2 }}>
              <CalendarErrorDisplay
                error={bookingsError}
                onRetry={handleRetryBookings}
                onDismiss={() => handleDismissError('bookings')}
                retryCount={retryState.attemptCount}
                maxRetries={3}
              />
            </Box>
          )}

          {/* Sync Progress */}
          {syncProgress.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <SyncProgressIndicator
                syncProgress={syncProgress}
                onCancel={handleCancelSync}
                onRetry={handleRetrySync}
                onDismiss={handleDismissSync}
              />
            </Box>
          )}

          {/* Filters Section */}
          {!isLoadingListings && listings.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <ListingMultiSelect
                  listings={listings}
                  selectedListings={selectedListings}
                  onChange={handleListingFilterChange}
                  loading={isLoadingListings}
                  error={listingsError ? String(listingsError) : undefined}
                />
                
                <Button
                  variant={includeExternal ? 'contained' : 'outlined'}
                  onClick={() => setIncludeExternal(!includeExternal)}
                  size="small"
                  color="secondary"
                >
                  {includeExternal ? 'Hide' : 'Show'} External Bookings
                </Button>
              </Box>
            </Box>
          )}

          {/* Loading state for filters */}
          {isLoadingListings && (
            <Box sx={{ mb: 4 }}>
              <FiltersSkeleton />
            </Box>
          )}

          {/* Conflicts Alert */}
          {conflicts.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Booking Conflicts Detected:</strong> {conflicts.length} conflict{conflicts.length > 1 ? 's' : ''} found between direct and external bookings.
                Please review and resolve these conflicts.
              </Typography>
            </Alert>
          )}

          {/* Calendar Grid */}
          <Box sx={{ mb: 4 }}>
            {isLoadingBookings ? (
              <CalendarGridSkeleton animate={!retryState.isRetrying} />
            ) : bookingsError && !dismissedErrors.has('bookings') ? (
              <Box sx={{ mb: 2 }}>
                <CalendarErrorDisplay
                  error={bookingsError}
                  onRetry={handleRetryBookings}
                  onDismiss={() => handleDismissError('bookings')}
                  retryCount={retryState.attemptCount}
                  maxRetries={3}
                />
              </Box>
            ) : listings.length === 0 && !isLoadingListings ? (
              /* Show empty state when no listings exist */
              <EmptyCalendarState 
                type="no-listings"
                onCreateListing={() => navigate('/listings/create')}
              />
            ) : listings.length > 0 && selectedListings.length === 0 && !isLoadingListings ? (
              /* Show empty state when no listings are selected */
              <Box>
                <CalendarGrid
                  currentMonth={currentMonth}
                  selectedDate={selectedDate}
                  bookings={[]} // Empty bookings array
                  onDateSelect={handleDateSelect}
                  onMonthChange={handleMonthChange}
                />
                <Box sx={{ mt: 3 }}>
                  <EmptyCalendarState 
                    type="no-selected-listings"
                  />
                </Box>
              </Box>
            ) : (
              <>
                <CalendarGrid
                  currentMonth={currentMonth}
                  selectedDate={selectedDate}
                  bookings={allBookings}
                  onDateSelect={handleDateSelect}
                  onMonthChange={handleMonthChange}
                />
                
                {/* Show empty state when no bookings found */}
                {listings.length > 0 && selectedListings.length > 0 && allBookings.length === 0 && !isLoadingBookings && (
                  <Box sx={{ mt: 3 }}>
                    <EmptyCalendarState 
                      type="no-bookings"
                      currentMonth={currentMonth}
                    />
                  </Box>
                )}
              </>
            )}

            {/* Loading indicator for retry operations */}
            {retryState.isRetrying && (
              <LoadingMessage message="Refreshing calendar data..." />
            )}
          </Box>

          {/* Calendar Sync Settings */}
          <Collapse in={showSyncSettings}>
            <Card sx={{ mb: 4 }}>
              <CardContent>
                <CalendarSyncSettings
                  listings={listings}
                  onSyncComplete={handleSyncComplete}
                  onSyncProgress={handleSyncProgress}
                />
              </CardContent>
            </Card>
          </Collapse>

          {/* Navigation Demo */}
          {showNavigationDemo && (
            <Box sx={{ mt: 4 }}>
              <NavigationDemo />
            </Box>
          )}

          {/* Day Summary Panel */}
          {selectedDate && (
            <DaySummaryPanel
              selectedDate={selectedDate}
              bookings={bookingsForSelectedDate}
              open={showDaySummary}
              onClose={handleCloseDaySummary}
              onBookingClick={handleBookingClick}
            />
          )}

          {/* Success Snackbar */}
          <Snackbar
            open={showSuccessMessage}
            autoHideDuration={4000}
            onClose={() => setShowSuccessMessage(false)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert 
              onClose={() => setShowSuccessMessage(false)} 
              severity="success" 
              sx={{ width: '100%' }}
            >
              {successMessage}
            </Alert>
          </Snackbar>
        </SidebarLayout>
      </OfflineDetector>
    </CalendarErrorBoundary>
  )
}